import { Link } from "react-router-dom";
import { CircleAlert, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CaseStatusValue, ProcessReadinessGapView } from "@/types";

// #300 (Review LOW-14): gemeinsame Bausteine der Prozess-Seiten — vorher zyklisch
// zwischen Processes/Detail und ProcessInstances/Detail importiert.

// Behebbarkeits-Links: Bereichs-Seiten (Template- UND Instanz-Gap-Codes).
export function gapLink(gap: ProcessReadinessGapView): string | null {
  if (gap.code.startsWith("ROLE_")) return "/roles";
  if (gap.code.startsWith("CONNECTOR_")) return "/connectors";
  if (gap.code.startsWith("PLUGIN_") || gap.code.startsWith("SKILL_")) return "/plugins";
  if (gap.code.startsWith("AGENT_") || gap.code === "FIXED_AGENT_NOT_SERVICE") return "/agents";
  return null;
}

export function GapRow({ gap }: { gap: ProcessReadinessGapView }) {
  const link = gapLink(gap);
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      {gap.severity === "block" ? (
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
      ) : (
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
      )}
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-text">
          <span className="font-mono">{gap.code}</span>
          <span className="mx-1.5 text-text3">·</span>
          <span className="font-mono text-text2">{gap.target}</span>
        </div>
        <div className="text-[12px] text-text2">
          {gap.message}
          {link && (
            <>
              {" "}
              <Link to={link} className="text-accent hover:underline">
                beheben →
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function InstanceStatusBadge({ status }: { status: "draft" | "ready" | "not_ready" | "paused" }) {
  switch (status) {
    case "ready":
      return <Badge variant="active">bereit</Badge>;
    case "not_ready":
      return <Badge variant="busy">nicht bereit</Badge>;
    case "paused":
      return <Badge variant="default">pausiert</Badge>;
    default:
      return <Badge variant="default">Entwurf</Badge>;
  }
}

// --- #301 (UC-5b): Bausteine der Lauf-Sicht ------------------------------------------------

// Die deutschen Namen der Lauf-Zustände. Ein Betrachter soll lesen, WORAUF ein Lauf wartet —
// „waiting_approval" sagt ihm nichts, „wartet auf Freigabe" sagt ihm, dass er dran ist.
const CASE_STATUS: Record<CaseStatusValue, { label: string; variant: "active" | "busy" | "destructive" | "default" | "online" }> = {
  running: { label: "läuft", variant: "online" },
  waiting_input: { label: "wartet auf Eingabe", variant: "busy" },
  waiting_approval: { label: "wartet auf Freigabe", variant: "busy" },
  blocked: { label: "blockiert", variant: "destructive" },
  paused: { label: "pausiert", variant: "default" },
  done: { label: "abgeschlossen", variant: "active" },
  aborted: { label: "abgebrochen", variant: "default" },
};

export function CaseStatusBadge({ status }: { status: CaseStatusValue }) {
  const s = CASE_STATUS[status] ?? { label: status, variant: "default" as const };
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

const STEP_FARBE: Record<string, string> = {
  done: "bg-ok",
  running: "bg-accent",
  waiting: "bg-warn",
  blocked: "bg-danger",
  skipped: "bg-muted",
  pending: "bg-border-strong",
};

// #303 (UC-2): die Schritt-Zustände in Klartext — EINE Quelle für Timeline, Graph und
// die Schritt-Kette. Vorher stand die Tabelle als lokale Kopie in `pages/Cases/Detail`.
export const SCHRITT_LABEL: Record<string, string> = {
  pending: "offen",
  running: "läuft",
  waiting: "wartet",
  blocked: "blockiert",
  done: "erledigt",
  skipped: "übersprungen",
};

const SCHRITT_VARIANT: Record<string, "active" | "busy" | "destructive" | "default" | "online" | "outline"> = {
  done: "active",
  running: "online",
  waiting: "busy",
  blocked: "destructive",
  skipped: "default",
  pending: "outline",
};

export function StepStatusBadge({ status }: { status: string }) {
  return <Badge variant={SCHRITT_VARIANT[status] ?? "default"}>{SCHRITT_LABEL[status] ?? status}</Badge>;
}

/** Wer den Schritt ausführt — die Spec-Rolle in Klartext. `null` = Vorlage nicht lesbar. */
export function akteurLabel(akteur: string | null): string | null {
  if (akteur === "agent") return "Agent";
  if (akteur === "mensch") return "Mensch";
  if (akteur === "grenzagent") return "Agent an der Aussengrenze";
  return akteur;
}

/**
 * Der Fortschritt als Schritt-Kette. Bewusst kein Prozentbalken: ein Prozess hat benannte
 * Schritte, und „bei welchem steht er?" ist die Frage, die jemand tatsächlich hat.
 */
export function StepProgress({
  steps,
  currentStepId,
}: {
  steps: Array<{ id: string; status: string }>;
  currentStepId: string | null;
}) {
  return (
    <div className="flex items-center gap-1" data-testid="case-progress">
      {steps.map((s) => (
        <span
          key={s.id}
          title={`${s.id}: ${s.status}`}
          className={`h-1.5 w-6 rounded-full ${STEP_FARBE[s.status] ?? "bg-border-strong"} ${
            s.id === currentStepId ? "ring-2 ring-accent-border" : ""
          }`}
        />
      ))}
    </div>
  );
}
